<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import countries from 'shared/constants/countries.js';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
  // Dentro de um modal que já diz "Novo contato" no topo.
  semTitulo: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

// 🔴 NOME DE CLIENTE VEM DA FICHA (Victor, 21/09/2026).
//
// Mesma regra do painel do chat, aplicada onde o nome também se edita: quando
// o contato é cliente, esse nome é o do cadastro — o que sai na petição, no
// contrato e na cobrança. Aqui os campos ficam desligados, com o recado ao
// lado dizendo onde se edita.
//
// `is_legal_client` é o sinal estrutural mantido por
// Contact#sync_is_legal_client_badge — o mesmo que desenha o selo "Cliente".
// Contato NOVO nunca cai aqui: ele ainda não é cliente de ninguém.
const nomeTravado = computed(
  () => !!props.contactData?.customAttributes?.is_legal_client
);

const ehCampoDeNome = chave => ['FIRST_NAME', 'LAST_NAME'].includes(chave);

const FORM_CONFIG = {
  FIRST_NAME: { field: 'firstName' },
  LAST_NAME: { field: 'lastName' },
  EMAIL_ADDRESS: { field: 'email' },
  PHONE_NUMBER: { field: 'phoneNumber' },
  CITY: { field: 'additionalAttributes.city' },
  COUNTRY: { field: 'additionalAttributes.countryCode' },
  BIO: { field: 'additionalAttributes.description' },
  COMPANY_NAME: { field: 'additionalAttributes.companyName' },
};

// advup — só as redes importantes (removidos Facebook, Twitter/X, Github a pedido)
const SOCIAL_CONFIG = {
  INSTAGRAM: 'i-ri-instagram-line',
  LINKEDIN: 'i-ri-linkedin-box-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TELEGRAM: 'i-ri-telegram-fill',
};

const defaultState = {
  id: 0,
  name: '',
  email: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
};

const state = reactive({ ...defaultState });

const validationRules = {
  firstName: { required },
  email: { email },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    additionalAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';

  Object.assign(state, {
    id,
    name,
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
  });
};

const countryOptions = computed(() =>
  countries.map(({ name, id }) => ({ label: name, value: id }))
);

// Cada campo com o RÓTULO visível (05/10/2026): o formulário só tinha o texto
// de exemplo, que some ao digitar — nos dois campos do nome sobrava "Maria
// Teste da" e "Silva", sem dizer qual era qual.
const editDetailsForm = computed(() =>
  Object.keys(FORM_CONFIG).map(key => ({
    key,
    label: t(`CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.LABEL`),
    placeholder: t(
      `CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM.${key}.PLACEHOLDER`
    ),
  }))
);

const tituloDoFormulario = computed(() =>
  props.isNewContact
    ? t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.NEW_TITLE')
    : t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.TITLE')
);

// Redes sociais recolhidas (Victor, 05/10/2026): o escritório não vive delas;
// abertas por padrão só quando o contato já tem alguma preenchida.
const temRedeSocial = computed(() =>
  Object.values(state.additionalAttributes?.socialProfiles || {}).some(valor =>
    String(valor || '').trim()
  )
);

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return ['firstName', 'email'].includes(field);
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      const isFormValid = await v$.value.$validate();
      if (isFormValid) {
        const { firstName, lastName, ...stateWithoutNames } = state;
        emit('update', stateWithoutNames);
      }
    },
  });
};

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const handleCountrySelection = value => {
  const selectedCountry = countries.find(option => option.id === value);
  state.additionalAttributes.country = selectedCountry?.name || '';
  emit('update', state);
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, defaultState);
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-col items-start gap-2">
      <span v-if="!semTitulo" class="py-1 text-sm font-medium text-n-slate-12">
        {{ tituloDoFormulario }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <div
            v-if="item.key === 'COUNTRY'"
            class="flex flex-col min-w-0 gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ item.label }}
            </span>
            <ComboBox
              v-model="state.additionalAttributes.countryCode"
              :options="countryOptions"
              :placeholder="item.placeholder"
              class="[&>div>button]:h-8"
              :class="{
                '[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:!outline-transparent':
                  !isDetailsView,
                '[&>div>button]:!bg-n-alpha-black2': isDetailsView,
              }"
              @update:model-value="handleCountrySelection"
            />
          </div>
          <div
            v-else-if="item.key === 'PHONE_NUMBER'"
            class="flex flex-col min-w-0 gap-1"
          >
            <span class="mb-0.5 text-heading-3 text-n-slate-12">
              {{ item.label }}
            </span>
            <PhoneNumberInput
              v-model="getFormBinding(item.key).value"
              :rotulo="item.label"
              :placeholder="item.placeholder"
              :show-border="isDetailsView"
            />
          </div>
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :label="item.label"
            :placeholder="item.placeholder"
            :disabled="nomeTravado && ehCampoDeNome(item.key)"
            :message="
              nomeTravado && item.key === 'FIRST_NAME'
                ? t('CONTACT_PANEL.NAME_FROM_CLIENT_FILE')
                : ''
            "
            :message-type="getMessageType(item.key)"
            :custom-input-class="`h-8 !pt-1 !pb-1 ${
              !isDetailsView
                ? '[&:not(.error,.focus)]:!outline-transparent'
                : ''
            }`"
            class="w-full"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
      </div>
    </div>
    <details class="cf-redes" :open="temRedeSocial || undefined">
      <summary class="cf-redes__titulo">
        <i class="i-ph-caret-right cf-redes__seta" aria-hidden="true" />
        {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.TITLE') }}
        <span class="cf-redes__opcional">
          {{ t('CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.OPCIONAL') }}
        </span>
      </summary>
      <div class="flex flex-wrap gap-2 pt-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex items-center h-8 gap-2 px-2 rounded-lg"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-auto min-w-[100px] text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :aria-label="item.placeholder"
            :size="item.placeholder.length"
            @input="emit('update', state)"
          />
        </div>
      </div>
    </details>
  </div>
</template>

<style scoped>
.cf-redes__titulo {
  display: inline-flex;
  align-items: center;
  gap: var(--esp-2);
  padding: var(--esp-1) 0;
  color: rgb(var(--slate-12));
  font-size: var(--txt-corpo);
  font-weight: 500;
  list-style: none;
  cursor: pointer;
}

.cf-redes__titulo::-webkit-details-marker {
  display: none;
}

.cf-redes__seta {
  width: 14px;
  height: 14px;
  color: rgb(var(--slate-10));
  transition: transform var(--mov-1) var(--mov-curva);
}

.cf-redes[open] .cf-redes__seta {
  transform: rotate(90deg);
}

.cf-redes__opcional {
  color: rgb(var(--slate-10));
  font-size: var(--txt-nota);
  font-weight: 400;
}
</style>
